<template>
    <BasePanel 
        headline="lang_CONTRACTS_PUBLISHING_CAPTION_SUB_CATALOGUES"
        :alvqa="alvqa"
        :onOpenPanel="onOpenPanel"
    >
        <template v-slot:panelContent>
            <v-data-table         
                :headers="headers"
                :items="item"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
                :items-per-page="itemsPerPage"
                :page.sync="currentPage"
                :server-items-length="totalRecords"
                :loading="isLoading"
                :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                id="listPage"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row class="pb-3">
                        <v-spacer></v-spacer>
                        <v-col cols="3">
                            <BaseButton 
                                @click="addSubCatalogue"
                                label="lang_CONTRACTS_PUBLISHING_CAPTION_ADD_SUB_CATALOGUE"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

                <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

                <!-- Items -->
                <template v-slot:[`item.name`]="{ item }">
                    <div div class="ellipsis">
                        <BaseTooltip>
                            <template v-slot:text>{{ getCatalogueName(item) + ' (' + $t('lang_GENERIC_CAPTION_ALVID') + ': ' + item.licensor.alvId + ' | ' + getIPI(item) + (getKTO(item) ? ' | ' + getKTO(item) : '') + ')' }}</template>
                            <template v-slot:content>
                                <a v-if="$alvPlugin.createUrlForAlvId(item.licensor.alvId)" :href="$alvPlugin.createUrlForAlvId(item.licensor.alvId)" target="_blank" :alvqa="alvqa + '-list-row-' + item.id + '-text-name'">
                                    {{ getCatalogueName(item) }}
                                </a>
                                <span v-else :alvqa="alvqa + '-list-row-' + item.id + '-text-name'">
                                    {{ getCatalogueName(item) }}
                                </span>
                            </template>
                        </BaseTooltip>
                    </div>
                </template>
                <template v-slot:[`item.gemaAgreementNo`]="{ item }">
                    <div class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.id + '-text-agreement-number'">
                        {{ item.gemaAgreementNo }}
                    </div>
                </template>
                <template v-slot:[`item.validUntil`]="{ item }">
                    <div class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.id + '-text-valid-until'">
                        {{ formatDate(item.validUntil) }}
                    </div>
                </template>
                <template v-slot:[`item.processableUntil`]="{ item }">
                    <div class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.id + '-text-processable-until'">
                        {{ formatDate(item.processableUntil) }}
                    </div>
                </template>
                <template v-slot:[`item.alvId`]="{ item }">
                    <div class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.id + '-text-alvid'">
                        {{ item.alvId }}
                    </div>
                </template>
                <!-- Buttons -->
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteSubCatalogue(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-' + item.id + '-icon-delete'"
                    />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-' + item.id + '-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editSubCatalogue(item)"
                    />
                </template>
            </v-data-table>
            <v-pagination   
                v-if="numberOfPages > 1"
                total-visible="10" 
                v-model="currentPage" 
                :length="numberOfPages"
                color="pagination"
                @input="nextPage()"
            ></v-pagination>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseTooltip from '@/components/_base/BaseTooltip'

export default {
    name: "PublishingContractsSubCataloguesReadOnlyPanel",
    components: {
        BasePanel,
        BaseButton,
        BaseIcon,
        BaseTooltip
    },
    props: {
        contractAvlId: {
            type: String
        }
    },
    data() {
        return {
            alvqa: 'publishing-contracts-list-row-' + this.contractAvlId + '-read-only-panel-sub-catalogues'
        }
    },
    computed: {
        item() {
            return this.$store.getters.getPublishingContractsSubCatalogues
        },
        headers() {
            return [
                {
                    text: this.$t("lang_GENERIC_CAPTION_ALVID"), 
                    align: "left", 
                    value: "alvId",
                    sortable: false,
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-alvid'
                },
                {
                    text: this.$t("lang_CAPTION_NAME"), 
                    align: "left", 
                    value: "name",
                    sortable: false,
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-name',
                },
                {
                    text: this.$t("lang_CONTRACTS_PUBLISHING_CAPTION_AGREEMENT_NUMBER"), 
                    align: "left", 
                    value: "gemaAgreementNo",
                    sortable: false,
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-agreement-number'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_LICENSABLE_UNTIL"), 
                    align: "left", 
                    value: "validUntil",
                    sortable: false,
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-valid-until'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_PROCESSABLE_UNTIL"), 
                    align: "left", 
                    value: "processableUntil",
                    sortable: false,
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-processable-until'
                },
                {
                    text: this.$t("lang_PUBLISHING_CAPTION_WORKS"), 
                    align: "right", 
                    value: "numberOfAssignedWorks",
                    sortable: false,
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-number-of-assigned-works'
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    sortable: false,
                    width: "70px",
                    value: "delete",
                    class: 'text-no-wrap'
                }
            ]
        },
        isLoading() { return this.$store.getters.getIsLoading },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        numberOfPages() { return this.$store.getters.getNumberOfPages },
        totalRecords() { return this.$store.getters.getTotalRecords },
        currentPage: {
            get() { return this.$store.getters.getCurrentPage },
            set(page) { this.$store.commit('setCurrentPage', page)}
        }        
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return ''
            try {
                return this.$d(new Date(dateString), 'alvDateFormat')
            } catch (e) {
                return dateString
            }
        },
        getCatalogueName(subcatalogue) {
            if(subcatalogue.licensor.group !== null && subcatalogue.licensor.group !== '') return subcatalogue.licensor.group
            else return subcatalogue.licensor.gemaName
        },
        getIPI(item) {
            let temp = item.licensor.ipi
            if(temp === null) {
                temp = this.$t('lang_GENERIC_CAPTION_NOMEN_NOMINANDUM')
            }
            return this.$t('lang_GENERIC_AZ_CAPTION_IPI') + ': ' + temp
        },
        getKTO(item) {
            let yKTO = item.licensor.ykto
            let hKTO = item.licensor.hkto
            let uKTO = item.licensor.ukto
            const isEmpty = (kto) => kto === null || kto === ''
            if(isEmpty(yKTO) && isEmpty(hKTO) && isEmpty(uKTO)) {
                return ''
            }
            if(isEmpty(yKTO)) {
                yKTO = this.$t('lang_GENERIC_CAPTION_NOMEN_NOMINANDUM')
            }
            if(isEmpty(hKTO)) {
                hKTO = this.$t('lang_GENERIC_CAPTION_NOMEN_NOMINANDUM')
            }
            if(isEmpty(uKTO)) {
                uKTO = this.$t('lang_GENERIC_CAPTION_NOMEN_NOMINANDUM')
            }
            return this.$t('lang_GENERIC_CAPTION_KTO') + ': ' + yKTO + '-' + hKTO + '-' + uKTO
        },
        addSubCatalogue() {
            this.$store.commit('setShowEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultPublishingContractsSubCatalogues)) 
            this.$store.commit('setPublishingContractsSubCataloguesEditingDialogDetails', defaultValues)
        },
        deleteSubCatalogue(item) {
            this.$store.commit("setSelectedPublishingContractsSubCatalogues", item.alvId)
            // this.$store.commit('setDeletionDialogContext', 'SelectedPublishingContractsSubCatalogues')
            this.$store.commit('setShowDeletionDialog', true)
        },
        editSubCatalogue(item) {
            this.$store.commit('setSelectedPublishingContractsSubCatalogues', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            // prepare data for editing dialog
            this.$store.commit('setPublishingContractsSubCataloguesLicensorOptions', [item.licensor])

            // set the copy in dialog details to edit
            this.$store.commit('setPublishingContractsSubCataloguesEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowEditingDialog', true) 
        },
        async nextPage() { 
            await this.$store.dispatch('fetchPublishingContractsSubCatalogues') 
            this.$vuetify.goTo('#listPage')
        },
        onOpenPanel() {
            this.$store.dispatch('fetchPublishingContractsSubCatalogues')
        },
    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
    }
</style>